import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  BookOpen,
  User,
  ChevronRight,
  LogOut,
  Settings,
  BookMarked,
  UserCircle,
  Clock,
  LayoutDashboard,
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useSettings } from '@/context/SettingsContext';
import { Link, useLocation } from 'react-router-dom';

export function HeaderComponent() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const { isAuthenticated, user, logout } = useAuth();
  const { settings } = useSettings();
  const location = useLocation();
  const isAdmin = user?.role === 'ADMIN';
  const isLibraryProfile = settings.usage_profile === 'library';

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);

  const handleLogout = async () => {
    await logout();
  };

  const isActiveRoute = (path: string) => {
    return location.pathname === path;
  };

  const navLinkClass = (path: string) =>
    `relative px-3 py-2 text-sm font-medium transition-all duration-200 rounded-lg ${
      isActiveRoute(path)
        ? 'text-white bg-white/10'
        : 'text-slate-300 hover:text-white hover:bg-white/5'
    }`;

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-slate-900/95 backdrop-blur-lg shadow-lg border-b border-slate-800/50'
          : 'bg-slate-900'
      }`}
    >
      <div className="container mx-auto px-4 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo & Title */}
          <Link to="/" className="flex items-center space-x-3 group">
            <div className="relative">
              {settings.logo_url ? (
                <img
                  src={settings.logo_url}
                  alt={settings.site_name || 'Logo'}
                  className="h-9 w-auto object-contain"
                />
              ) : (
                <>
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-lg blur opacity-50 group-hover:opacity-75 transition-opacity"></div>
                  <div className="relative bg-gradient-to-r from-blue-500 to-indigo-500 p-2 rounded-lg">
                    <BookOpen className="h-5 w-5 text-white" />
                  </div>
                </>
              )}
            </div>
            <span className="text-lg font-bold text-white tracking-tight">
              {settings.site_name || 'Folio'}
              <span className="text-blue-400">Library</span>
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-1">
            <Link to="/" className={navLinkClass('/')}>
              Home
            </Link>
            <Link to="/books" className={navLinkClass('/books')}>
              Books
            </Link>
            <Link to="/authors" className={navLinkClass('/authors')}>
              Authors
            </Link>
            {isAuthenticated && isLibraryProfile && (
              <Link
                to="/my-collection"
                className={navLinkClass('/my-collection')}
              >
                My Collection
              </Link>
            )}
            {settings.show_about_page && (
              <Link to="/about" className={navLinkClass('/about')}>
                About
              </Link>
            )}
            {settings.show_contact_page && (
              <Link to="/contact" className={navLinkClass('/contact')}>
                Contact
              </Link>
            )}
          </nav>

          {/* User Menu (Desktop) */}
          <div className="hidden md:flex items-center space-x-3">
            {isAuthenticated ? (
              <div className="flex items-center space-x-3">
                <span className="text-sm font-medium text-slate-300">
                  {user?.name || 'User'}
                </span>
                <DropdownMenu modal={false}>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      className="relative h-10 w-10 rounded-full bg-gradient-to-r from-blue-500/20 to-indigo-500/20 hover:from-blue-500/30 hover:to-indigo-500/30 border border-slate-700"
                    >
                      <User className="h-5 w-5 text-slate-200" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56 mt-2">
                    <DropdownMenuLabel className="font-normal">
                      <div className="flex flex-col space-y-1">
                        <p className="text-sm font-medium">
                          {user?.name || 'User'}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {user?.email}
                        </p>
                      </div>
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem asChild>
                      <Link
                        to="/profile"
                        className="flex items-center cursor-pointer"
                      >
                        <UserCircle className="mr-2 h-4 w-4" />
                        Profile
                      </Link>
                    </DropdownMenuItem>
                    {isLibraryProfile && (
                      <DropdownMenuItem asChild>
                        <Link
                          to="/my-collection"
                          className="flex items-center cursor-pointer"
                        >
                          <BookMarked className="mr-2 h-4 w-4" />
                          My Collection
                        </Link>
                      </DropdownMenuItem>
                    )}
                    {isLibraryProfile && settings.loans_enabled && (
                      <DropdownMenuItem asChild>
                        <Link
                          to="/my-loans"
                          className="flex items-center cursor-pointer"
                        >
                          <Clock className="mr-2 h-4 w-4" />
                          My Loans
                        </Link>
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem asChild>
                      <Link
                        to="/profile"
                        className="flex items-center cursor-pointer"
                      >
                        <Settings className="mr-2 h-4 w-4" />
                        Settings
                      </Link>
                    </DropdownMenuItem>
                    {isAdmin && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem asChild>
                          <Link
                            to="/admin"
                            className="flex items-center cursor-pointer text-red-600 focus:text-red-600"
                          >
                            <LayoutDashboard className="mr-2 h-4 w-4" />
                            Admin Dashboard
                          </Link>
                        </DropdownMenuItem>
                      </>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={handleLogout}
                      className="text-red-500 focus:text-red-500 cursor-pointer"
                    >
                      <LogOut className="mr-2 h-4 w-4" />
                      Logout
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            ) : (
              <div className="flex items-center space-x-2">
                <Button
                  variant="ghost"
                  asChild
                  className="text-slate-300 hover:text-white hover:bg-white/5"
                >
                  <Link to="/login">Login</Link>
                </Button>
                <Button
                  asChild
                  className="bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600 text-white shadow-lg shadow-blue-500/25 border-0"
                >
                  <Link to="/signup">Sign Up</Link>
                </Button>
              </div>
            )}
          </div>

          {/* Mobile Menu Button */}
          <Button
            variant="ghost"
            size="icon"
            className="md:hidden text-slate-300 hover:text-white hover:bg-white/10"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
          >
            <div className="relative w-6 h-6">
              <span
                className={`absolute block h-0.5 w-6 bg-current transform transition-all duration-300 ${isMenuOpen ? 'rotate-45 top-3' : 'top-1'}`}
              ></span>
              <span
                className={`absolute block h-0.5 w-6 bg-current top-3 transition-all duration-300 ${isMenuOpen ? 'opacity-0' : 'opacity-100'}`}
              ></span>
              <span
                className={`absolute block h-0.5 w-6 bg-current transform transition-all duration-300 ${isMenuOpen ? '-rotate-45 top-3' : 'top-5'}`}
              ></span>
            </div>
          </Button>
        </div>

        {/* Mobile Menu */}
        <div
          className={`md:hidden overflow-hidden transition-all duration-300 ease-in-out ${
            isMenuOpen ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
          }`}
        >
          <div className="py-4 border-t border-slate-800">
            <nav className="flex flex-col space-y-1">
              <Link
                to="/"
                className={`flex items-center justify-between px-4 py-3 rounded-lg transition-colors ${
                  isActiveRoute('/')
                    ? 'bg-white/10 text-white'
                    : 'text-slate-300 hover:bg-white/5 hover:text-white'
                }`}
              >
                <span>Home</span>
                <ChevronRight className="h-4 w-4 opacity-50" />
              </Link>
              <Link
                to="/books"
                className={`flex items-center justify-between px-4 py-3 rounded-lg transition-colors ${
                  isActiveRoute('/books')
                    ? 'bg-white/10 text-white'
                    : 'text-slate-300 hover:bg-white/5 hover:text-white'
                }`}
              >
                <span>Books</span>
                <ChevronRight className="h-4 w-4 opacity-50" />
              </Link>
              <Link
                to="/authors"
                className={`flex items-center justify-between px-4 py-3 rounded-lg transition-colors ${
                  isActiveRoute('/authors')
                    ? 'bg-white/10 text-white'
                    : 'text-slate-300 hover:bg-white/5 hover:text-white'
                }`}
              >
                <span>Authors</span>
                <ChevronRight className="h-4 w-4 opacity-50" />
              </Link>
              {isAuthenticated && isLibraryProfile && (
                <Link
                  to="/my-collection"
                  className={`flex items-center justify-between px-4 py-3 rounded-lg transition-colors ${
                    location.pathname.startsWith('/my-collection')
                      ? 'bg-white/10 text-white'
                      : 'text-slate-300 hover:bg-white/5 hover:text-white'
                  }`}
                >
                  <span>My Collection</span>
                  <ChevronRight className="h-4 w-4 opacity-50" />
                </Link>
              )}
              {settings.show_about_page && (
                <Link
                  to="/about"
                  className={`flex items-center justify-between px-4 py-3 rounded-lg transition-colors ${
                    isActiveRoute('/about')
                      ? 'bg-white/10 text-white'
                      : 'text-slate-300 hover:bg-white/5 hover:text-white'
                  }`}
                >
                  <span>About</span>
                  <ChevronRight className="h-4 w-4 opacity-50" />
                </Link>
              )}
              {settings.show_contact_page && (
                <Link
                  to="/contact"
                  className={`flex items-center justify-between px-4 py-3 rounded-lg transition-colors ${
                    isActiveRoute('/contact')
                      ? 'bg-white/10 text-white'
                      : 'text-slate-300 hover:bg-white/5 hover:text-white'
                  }`}
                >
                  <span>Contact</span>
                  <ChevronRight className="h-4 w-4 opacity-50" />
                </Link>
              )}
            </nav>

            <div className="mt-4 pt-4 border-t border-slate-800">
              {isAuthenticated ? (
                <div className="space-y-2">
                  <div className="flex items-center space-x-3 px-4 py-2 text-slate-300">
                    <div className="h-10 w-10 rounded-full bg-gradient-to-r from-blue-500/20 to-indigo-500/20 flex items-center justify-center border border-slate-700">
                      <User className="h-5 w-5" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-white">
                        {user?.name || 'User'}
                      </p>
                      <p className="text-xs text-slate-400">{user?.email}</p>
                    </div>
                  </div>
                  <Link
                    to="/profile"
                    className="flex items-center px-4 py-2 text-slate-300 hover:text-white hover:bg-white/5 rounded-lg transition-colors"
                  >
                    <UserCircle className="h-4 w-4 mr-3" />
                    Profile
                  </Link>
                  {isLibraryProfile && (
                    <Link
                      to="/my-collection"
                      className="flex items-center px-4 py-2 text-slate-300 hover:text-white hover:bg-white/5 rounded-lg transition-colors"
                    >
                      <BookMarked className="h-4 w-4 mr-3" />
                      My Collection
                    </Link>
                  )}
                  {isLibraryProfile && settings.loans_enabled && (
                    <Link
                      to="/my-loans"
                      className="flex items-center px-4 py-2 text-slate-300 hover:text-white hover:bg-white/5 rounded-lg transition-colors"
                    >
                      <Clock className="h-4 w-4 mr-3" />
                      My Loans
                    </Link>
                  )}
                  <Link
                    to="/profile"
                    className="flex items-center px-4 py-2 text-slate-300 hover:text-white hover:bg-white/5 rounded-lg transition-colors"
                  >
                    <Settings className="h-4 w-4 mr-3" />
                    Settings
                  </Link>
                  {isAdmin && (
                    <Link
                      to="/admin"
                      className="flex items-center px-4 py-2 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg transition-colors"
                    >
                      <LayoutDashboard className="h-4 w-4 mr-3" />
                      Admin Dashboard
                    </Link>
                  )}
                  <button
                    onClick={handleLogout}
                    className="flex items-center w-full px-4 py-2 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg transition-colors"
                  >
                    <LogOut className="h-4 w-4 mr-3" />
                    Logout
                  </button>
                </div>
              ) : (
                <div className="space-y-2 px-4">
                  <Button
                    className="w-full bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600 text-white"
                    asChild
                  >
                    <Link to="/signup">Sign Up</Link>
                  </Button>
                  <Button className="w-full" variant="outline" asChild>
                    <Link to="/login">Login</Link>
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}
